import React from "react";
import { Clock, Inbox, Search, BadgeCheck, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

const steps = [
  { 
    key: "pending", 
    label: "Pending", 
    description: "Your claim is being submitted",
    Icon: Clock,
    color: "amber"
  },
  { 
    key: "received", 
    label: "Received", 
    description: "We've received your claim",
    Icon: Inbox,
    color: "blue"
  },
  { 
    key: "reviewing", 
    label: "Reviewing", 
    description: "Your claim is under review",
    Icon: Search,
    color: "indigo"
  },
  { 
    key: "claimed", 
    label: "Claimed", 
    description: "Your claim has been approved",
    Icon: BadgeCheck,
    color: "emerald"
  },
];

const getIndexFromStatus = (status) => {
  const idx = steps.findIndex((s) => s.key === status);
  return idx === -1 ? 0 : idx;
};

const colorConfigs = {
  amber: {
    bg: "bg-amber-500",
    gradient: "from-amber-400 to-orange-500",
    light: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    iconBg: "bg-amber-100",
  },
  blue: {
    bg: "bg-blue-500",
    gradient: "from-blue-400 to-cyan-500",
    light: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
    iconBg: "bg-blue-100",
  },
  indigo: {
    bg: "bg-indigo-500",
    gradient: "from-indigo-400 to-purple-500",
    light: "bg-indigo-50",
    border: "border-indigo-200",
    text: "text-indigo-700",
    iconBg: "bg-indigo-100",
  },
  emerald: {
    bg: "bg-emerald-500",
    gradient: "from-emerald-400 to-green-500",
    light: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-700",
    iconBg: "bg-emerald-100",
  },
  gray: {
    bg: "bg-gray-300",
    gradient: "from-gray-300 to-gray-400",
    light: "bg-gray-50",
    border: "border-gray-200",
    text: "text-gray-500",
    iconBg: "bg-gray-100",
  },
};

const ClaimProgress = ({ status = "pending", variant = "emerald" }) => {
  const current = getIndexFromStatus(status);

  return (
    <div className="w-full">
      <div className="relative">
        {/* Background Progress Line */}
        <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-200 rounded-full" />
        
        {/* Active Progress Line */}
        <motion.div
          className="absolute top-6 left-0 h-0.5 bg-gradient-to-r from-amber-400 via-blue-400 via-indigo-400 to-emerald-400 rounded-full"
          initial={{ width: 0 }}
          animate={{ 
            width: current === 0 ? "0%" : 
                   current === 1 ? "33%" : 
                   current === 2 ? "66%" : "100%"
          }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
        />

        {/* Steps Container */}
        <div className="relative flex justify-between items-start">
          {steps.map((step, idx) => {
            const isDone = idx < current;
            const isActive = idx === current;
            const isPending = idx > current;
            
            const config = isPending 
              ? colorConfigs.gray 
              : isActive 
              ? colorConfigs[step.color] 
              : colorConfigs[step.color];

            const Icon = step.Icon;

            return (
              <motion.div
                key={step.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="flex flex-col items-center flex-1 relative z-10"
              >
                {/* Step Node */}
                <motion.div
                  className={`relative flex items-center justify-center w-12 h-12 rounded-full ${
                    isActive
                      ? `bg-gradient-to-br ${config.gradient} shadow-lg`
                      : isDone
                      ? `bg-gradient-to-br ${config.gradient} shadow-md`
                      : "bg-gray-100 border-2 border-gray-300"
                  } transition-all duration-300`}
                  style={isActive ? {
                    boxShadow: `0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 0 0 4px ${
                      step.color === 'amber' ? 'rgba(245, 158, 11, 0.2)' :
                      step.color === 'blue' ? 'rgba(59, 130, 246, 0.2)' :
                      step.color === 'indigo' ? 'rgba(99, 102, 241, 0.2)' :
                      'rgba(16, 185, 129, 0.2)'
                    }`
                  } : {}}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {/* Pulsing Ring for Active Step */}
                  {isActive && (
                    <>
                      <motion.div
                        className={`absolute inset-0 rounded-full bg-gradient-to-br ${config.gradient}`}
                        animate={{
                          scale: [1, 1.5, 1.5],
                          opacity: [0.5, 0, 0],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeOut",
                        }}
                      />
                      <motion.div
                        className={`absolute inset-0 rounded-full bg-gradient-to-br ${config.gradient}`}
                        animate={{
                          scale: [1, 1.3, 1.3],
                          opacity: [0.3, 0, 0],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: 0.5,
                          ease: "easeOut",
                        }}
                      />
                    </>
                  )}

                  {/* Icon */}
                  <div className="relative z-10">
                    {isDone ? (
                      <CheckCircle2 
                        size={20} 
                        className="text-white" 
                      />
                    ) : (
                      <Icon 
                        size={20} 
                        className={isActive || isDone ? "text-white" : "text-gray-400"} 
                      />
                    )}
                  </div>

                  {/* Completion Checkmark Animation */}
                  {isDone && (
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 200, damping: 15 }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-white shadow-md"
                    >
                      <CheckCircle2 size={12} className="text-white" />
                    </motion.div>
                  )}
                </motion.div>

                {/* Step Label */}
                <div className="mt-3 text-center max-w-[120px]">
                  <motion.p
                    className={`text-xs font-semibold ${
                      isActive
                        ? config.text
                        : isDone
                        ? config.text
                        : "text-gray-500"
                    } transition-colors duration-300`}
                  >
                    {step.label}
                  </motion.p>
                  {isActive && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-[10px] text-gray-500 mt-0.5 leading-snug"
                    >
                      {step.description}
                    </motion.p>
                  )}
                  {isActive && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`inline-flex mt-2 px-2.5 py-1 rounded-full ${config.light} ${config.border} border`}
                    >
                      <p className={`text-[10px] font-medium whitespace-nowrap ${config.text}`}>
                        Current Step
                      </p>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ClaimProgress;
